import React, { useMemo } from 'react';
import './IndirectMethod_RecentSurveyHistory_SurveyResult.css';
import {
  calculateQuestionAverage,
  calculateOverallAverage,
  validateRatingsBreakdown,
  roundToTwo
} from '../utils/surveyCalculations';

const IndirectMethod_RecentSurveyHistory_SurveyResult = ({ survey, onBack }) => {
  
  // Development-only logging utility
  const isDevelopment = process.env.NODE_ENV === 'development';
  const devLog = (level, message, data) => {
    if (isDevelopment) {
      console[level](message, data);
    }
  };

  // ===== DATA FORMAT DETECTION FUNCTIONS =====
  
  // Format detection function for survey analysis data
  const detectDataFormat = (analysisResults) => {
    // Check for new format indicators
    const hasNewFormat = analysisResults?.distribution && analysisResults?.average_scores;
    // Check for old format indicators  
    const hasOldFormat = analysisResults?.questions && analysisResults?.overall_summary;
    
    if (hasNewFormat) return 'new';
    if (hasOldFormat) return 'old';
    return 'unknown';
  };

  // Detect sub-format within new format structure
  const detectNewFormatSubType = (distribution) => {
    devLog('log', '🔍 Detecting new format sub-type...');
    
    if (!distribution || typeof distribution !== 'object') {
      devLog('warn', '⚠️ Invalid distribution object');
      return 'unknown';
    }
    
    const questionKeys = Object.keys(distribution);
    if (questionKeys.length === 0) {
      devLog('warn', '⚠️ No questions found in distribution');
      return 'unknown';
    }
    
    // Examine first question to determine structure
    const firstQuestionKey = questionKeys[0];
    const firstQuestionData = distribution[firstQuestionKey];
    
    devLog('log', '📊 Analyzing first question structure:', {
      questionKey: firstQuestionKey.substring(0, 50) + '...',
      dataType: Array.isArray(firstQuestionData) ? 'array' : typeof firstQuestionData,
      dataKeys: Array.isArray(firstQuestionData) ? `[${firstQuestionData.length} items]` : Object.keys(firstQuestionData || {})
    });
    
    let subType;
    if (Array.isArray(firstQuestionData)) {
      subType = 'array_structure';    // Format B (English): [ratings_object, metadata]
      devLog('log', '✅ Detected array structure (English/numeric format)');
    } else if (typeof firstQuestionData === 'object' && firstQuestionData !== null) {
      subType = 'object_structure';   // Format C (Chinese): {label: count}
      devLog('log', '✅ Detected object structure (Chinese/localized format)');
    } else {
      subType = 'unknown';
      devLog('warn', '⚠️ Unknown question data structure');
    }
    
    devLog('log', `📋 Sub-format detection result: ${subType}`);
    return subType;
  };

  // TEST: Add testing function for detectNewFormatSubType
  const testDetectNewFormatSubType = () => {
    devLog('log', '🧪 Testing detectNewFormatSubType function...');
    
    // Test data structures
    const testCases = [
      {
        name: 'Chinese/Object Structure',
        data: {
          "【我掌握了金融知识与技能】": {
            "3. 一般": 3,
            "4. 有些同意": 14,
            "5. 完全同意": 18
          }
        },
        expected: 'object_structure'
      },
      {
        name: 'English/Array Structure', 
        data: {
          "Do you like the new cartridge?": [
            {"1": 1, "2": 2, "3": 10, "4": 11, "5": 1},
            1699
          ]
        },
        expected: 'array_structure'
      },
      {
        name: 'Empty Object',
        data: {},
        expected: 'unknown'
      },
      {
        name: 'Invalid Input',
        data: null,
        expected: 'unknown'
      }
    ];
    
    testCases.forEach(testCase => {
      devLog('log', `🔬 Testing: ${testCase.name}`);
      const result = detectNewFormatSubType(testCase.data);
      const passed = result === testCase.expected;
      devLog(passed ? 'log' : 'error', `${passed ? '✅' : '❌'} Expected: ${testCase.expected}, Got: ${result}`);
    });
    
    devLog('log', '🏁 detectNewFormatSubType testing complete');
  };

  // ===== DATA EXTRACTION FUNCTIONS =====
  
  // Extract ratings data from different sub-format structures
  const extractRatingsData = (questionData, subFormat) => {
    devLog('log', '🔍 Extracting ratings data...', {
      subFormat,
      dataType: Array.isArray(questionData) ? 'array' : typeof questionData
    });
    
    let ratingsObject = {};
    
    try {
      switch (subFormat) {
        case 'array_structure':
          // Format B: [ratings_object, metadata_number]
          if (Array.isArray(questionData) && questionData.length >= 1) {
            ratingsObject = questionData[0] || {};
            devLog('log', '✅ Extracted from array structure:', {
              arrayLength: questionData.length,
              ratingsKeys: Object.keys(ratingsObject),
              metadata: questionData[1] || 'none'
            });
          } else {
            devLog('warn', '⚠️ Array structure expected but data is invalid');
          }
          break;
          
        case 'object_structure':
          // Format C: {label: count, label: count, ...}
          if (typeof questionData === 'object' && questionData !== null && !Array.isArray(questionData)) {
            ratingsObject = questionData;
            devLog('log', '✅ Extracted from object structure:', {
              ratingsKeys: Object.keys(ratingsObject),
              ratingsCount: Object.keys(ratingsObject).length
            });
          } else {
            devLog('warn', '⚠️ Object structure expected but data is invalid');
          }
          break;
          
        default:
          devLog('warn', '⚠️ Unknown sub-format, returning empty object');
          ratingsObject = {};
      }
    } catch (error) {
      devLog('error', '🚨 Error extracting ratings data:', error);
      ratingsObject = {};
    }
    
    devLog('log', '🎯 Final extracted ratings:', ratingsObject);
    return ratingsObject;
  };

  // TEST: Add testing function for extractRatingsData
  const testExtractRatingsData = () => {
    devLog('log', '🧪 Testing extractRatingsData function...');
    
    const testCases = [
      {
        name: 'Array Structure (English)',
        questionData: [
          {"1": 1, "2": 2, "3": 10, "4": 11, "5": 1},
          1699
        ],
        subFormat: 'array_structure',
        expected: {"1": 1, "2": 2, "3": 10, "4": 11, "5": 1}
      },
      {
        name: 'Object Structure (Chinese)',
        questionData: {
          "3. 一般": 3,
          "4. 有些同意": 14,
          "5. 完全同意": 18
        },
        subFormat: 'object_structure',
        expected: {
          "3. 一般": 3,
          "4. 有些同意": 14,
          "5. 完全同意": 18
        }
      },
      {
        name: 'Empty Array',
        questionData: [],
        subFormat: 'array_structure',
        expected: {}
      },
      {
        name: 'Null Data',
        questionData: null,
        subFormat: 'object_structure',
        expected: {}
      },
      {
        name: 'Unknown Format',
        questionData: "invalid data",
        subFormat: 'unknown_format',
        expected: {}
      },
      {
        name: 'Array with Missing Ratings Object',
        questionData: [null, 1699],
        subFormat: 'array_structure',
        expected: {}
      }
    ];
    
    testCases.forEach(testCase => {
      devLog('log', `🔬 Testing: ${testCase.name}`);
      const result = extractRatingsData(testCase.questionData, testCase.subFormat);
      
      // Deep comparison for objects
      const resultKeys = Object.keys(result).sort();
      const expectedKeys = Object.keys(testCase.expected).sort();
      const keysMatch = JSON.stringify(resultKeys) === JSON.stringify(expectedKeys);
      
      let valuesMatch = true;
      if (keysMatch) {
        valuesMatch = resultKeys.every(key => result[key] === testCase.expected[key]);
      }
      
      const passed = keysMatch && valuesMatch;
      devLog(passed ? 'log' : 'error', `${passed ? '✅' : '❌'} Test result:`, {
        expected: testCase.expected,
        actual: result,
        keysMatch,
        valuesMatch
      });
    });
    
    devLog('log', '🏁 extractRatingsData testing complete');
  };

  // ===== RATING KEY DETECTION FUNCTIONS =====
  
  // STEP 1: Validate used mapping and check key compatibility
  const validateUsedMapping = (ratingsObject, usedMapping) => {
    devLog('log', '🔍 Validating used mapping availability and quality...');
    
    // Check if usedMapping exists
    if (!usedMapping) {
      devLog('warn', '⚠️ No used_mapping provided');
      return {
        status: 'no_mapping',
        error: 'No used_mapping provided',
        hasMapping: false
      };
    }
    
    // Check if usedMapping is valid object
    if (typeof usedMapping !== 'object' || Array.isArray(usedMapping)) {
      devLog('warn', '⚠️ Invalid used_mapping format');
      return {
        status: 'invalid_mapping_format',
        error: 'used_mapping is not a valid object',
        hasMapping: false
      };
    }
    
    // Check if usedMapping is empty
    const mappingKeys = Object.keys(usedMapping);
    if (mappingKeys.length === 0) {
      devLog('warn', '⚠️ Empty used_mapping provided');
      return {
        status: 'empty_mapping',
        error: 'used_mapping is empty',
        hasMapping: false,
        mappingKeys: []
      };
    }
    
    // Validate ratingsObject
    if (!ratingsObject || typeof ratingsObject !== 'object' || Array.isArray(ratingsObject)) {
      devLog('warn', '⚠️ Invalid ratings object for validation');
      return {
        status: 'invalid_ratings',
        error: 'Invalid ratings object',
        hasMapping: true,
        mappingKeys
      };
    }
    
    // Get extracted rating keys
    const ratingsKeys = Object.keys(ratingsObject);
    if (ratingsKeys.length === 0) {
      devLog('warn', '⚠️ No rating keys found in extracted data');
      return {
        status: 'no_rating_keys',
        error: 'No rating keys in extracted data',
        hasMapping: true,
        mappingKeys,
        ratingsKeys: []
      };
    }
    
    // Check key compatibility
    const validKeys = ratingsKeys.filter(key => mappingKeys.includes(key));
    const invalidKeys = ratingsKeys.filter(key => !mappingKeys.includes(key));
    
    devLog('log', `📊 Key compatibility analysis:`, {
      totalRatingKeys: ratingsKeys.length,
      totalMappingKeys: mappingKeys.length,
      validKeys: validKeys.length,
      invalidKeys: invalidKeys.length,
      ratingsKeys,
      mappingKeys,
      validKeysArray: validKeys,
      invalidKeysArray: invalidKeys
    });
    
    // Determine validation status
    if (validKeys.length === 0) {
      devLog('warn', '⚠️ No extracted keys match mapping keys');
      return {
        status: 'no_matching_keys',
        error: 'No extracted rating keys match the used_mapping keys',
        hasMapping: true,
        mappingKeys,
        ratingsKeys,
        validKeys: [],
        invalidKeys
      };
    }
    
    if (invalidKeys.length > 0) {
      devLog('warn', `⚠️ Some keys don't match mapping: ${invalidKeys.join(', ')}`);
      return {
        status: 'partial_match',
        warning: `Some keys not in mapping: ${invalidKeys.join(', ')}`,
        hasMapping: true,
        mappingKeys,
        ratingsKeys,
        validKeys,
        invalidKeys
      };
    }
    
    // All keys match!
    devLog('log', '✅ All extracted keys match used_mapping');
    return {
      status: 'valid',
      message: 'All extracted keys match used_mapping',
      hasMapping: true,
      mappingKeys,
      ratingsKeys,
      validKeys,
      invalidKeys: []
    };
  };
  
  // STEP 2: Smart adaptation - detect mismatch patterns and convert
  const detectMismatchPattern = (ratingsKeys, mappingKeys, usedMapping) => {
    devLog('log', '🔍 Analyzing key mismatch patterns...');
    
    // Check if keys don't match at all
    const matchingKeys = ratingsKeys.filter(key => mappingKeys.includes(key));
    if (matchingKeys.length === ratingsKeys.length) {
      devLog('log', '✅ No mismatch - all keys match mapping');
      return { type: 'no_mismatch' };
    }
    
    if (matchingKeys.length === 0) {
      devLog('log', '⚠️ Complete mismatch detected - analyzing patterns...');
      
      // Pattern 1: Letter grades (A,B,C,D,E) vs text labels
      if (isLetterGradePattern(ratingsKeys) && isTextLabelPattern(mappingKeys)) {
        devLog('log', '🎯 Detected: Letter grades vs text labels');
        return {
          type: 'letter_to_text',
          sourceKeys: ratingsKeys,
          targetKeys: mappingKeys,
          targetMapping: usedMapping
        };
      }
      
      // Pattern 2: Numeric keys vs text labels
      if (isNumericPattern(ratingsKeys) && isTextLabelPattern(mappingKeys)) {
        devLog('log', '🎯 Detected: Numeric keys vs text labels');
        return {
          type: 'numeric_to_text',
          sourceKeys: ratingsKeys,
          targetKeys: mappingKeys,
          targetMapping: usedMapping
        };
      }
      
      // Pattern 3: Same length ordinal mismatch (fallback)
      if (ratingsKeys.length === mappingKeys.length) {
        devLog('log', '🎯 Detected: Ordinal mismatch (same length)');
        return {
          type: 'ordinal_mismatch',
          sourceKeys: ratingsKeys,
          targetKeys: mappingKeys,
          targetMapping: usedMapping
        };
      }
    }
    
    devLog('warn', '❌ Partial mismatch or unrecognized pattern');
    return { 
      type: 'unrecognized',
      matchingKeys: matchingKeys.length,
      totalKeys: ratingsKeys.length
    };
  };
  
  // Helper functions for pattern detection
  const isLetterGradePattern = (keys) => {
    const letterGradeRegex = /^[A-F]$/;
    return keys.length >= 3 && keys.every(key => letterGradeRegex.test(key));
  };
  
  const isNumericPattern = (keys) => {
    return keys.every(key => !isNaN(key) && !isNaN(parseFloat(key)));
  };
  
  const isTextLabelPattern = (keys) => {
    return keys.every(key => 
      typeof key === 'string' && 
      key.length > 1 && 
      /[a-zA-Z]/.test(key)
    );
  };
  
  // Smart conversion functions
  const smartConvertRatings = (ratings, mismatchPattern) => {
    devLog('log', `🔄 Smart converting ratings using pattern: ${mismatchPattern.type}`);
    
    switch (mismatchPattern.type) {
      case 'letter_to_text':
        return convertLetterGradesToText(ratings, mismatchPattern.targetMapping);
      case 'numeric_to_text':
        return convertNumericToText(ratings, mismatchPattern.targetMapping);
      case 'ordinal_mismatch':
        return convertOrdinalMismatch(ratings, mismatchPattern);
      case 'no_mismatch':
        devLog('log', '✅ No conversion needed');
        return ratings;
      default:
        devLog('warn', '⚠️ No conversion available for this pattern');
        return null;
    }
  };
  
  // Convert letter grades (A,B,C,D,E) to text labels
  const convertLetterGradesToText = (ratings, usedMapping) => {
    devLog('log', '🔤 Converting letter grades to text labels...');
    
    // Sort mapping entries by numeric value (descending)
    const sortedMappingEntries = Object.entries(usedMapping)
      .sort(([,a], [,b]) => b - a);
    
    devLog('log', '📊 Sorted mapping entries:', sortedMappingEntries);
    
    // Create letter to text mapping (A=highest, E=lowest)
    const letterToTextMap = {};
    const letterGrades = ['A', 'B', 'C', 'D', 'E'];
    
    letterGrades.forEach((letter, index) => {
      if (sortedMappingEntries[index]) {
        letterToTextMap[letter] = sortedMappingEntries[index][0];
      }
    });
    
    devLog('log', '🗺️ Inferred letter-to-text mapping:', letterToTextMap);
    
    // Convert ratings using the inferred mapping
    const convertedRatings = {};
    Object.entries(ratings).forEach(([letterKey, count]) => {
      const textKey = letterToTextMap[letterKey];
      if (textKey) {
        convertedRatings[textKey] = count;
        devLog('log', `🔄 Converted: ${letterKey} (${count}) → ${textKey} (${count})`);
      } else {
        devLog('warn', `⚠️ No mapping found for letter: ${letterKey}`);
      }
    });
    
    devLog('log', '✅ Letter grade conversion complete:', convertedRatings);
    return convertedRatings;
  };
  
  // Convert numeric keys to text labels (for future use)
  const convertNumericToText = (ratings, usedMapping) => {
    devLog('log', '🔢 Converting numeric keys to text labels...');
    // Implementation for numeric-to-text conversion
    // For now, return null to indicate not implemented
    devLog('warn', '⚠️ Numeric-to-text conversion not yet implemented');
    return null;
  };
  
  // Convert ordinal mismatch (for future use)
  const convertOrdinalMismatch = (ratings, mismatchPattern) => {
    devLog('log', '🔄 Converting ordinal mismatch...');
    // Implementation for ordinal mismatch conversion
    // For now, return null to indicate not implemented
    devLog('warn', '⚠️ Ordinal mismatch conversion not yet implemented');
    return null;
  };
  
  // ===== PHASE 1: ENHANCED STRUCTURE DETECTION =====
  
  // Extract distribution data from different formats (Phase 1)
  const extractDistributionData = (questionData) => {
    devLog('log', '🔍 Extracting distribution data from format...');
    
    // Format A: Direct object {"3. 一般": 3, "4. 有些同意": 14} (structured_raw)
    if (typeof questionData === 'object' && !Array.isArray(questionData)) {
      devLog('log', '📊 Format A detected: Direct object format');
      return questionData;
    }
    
    // Format B: Array [{"A": 1, "B": 11}, metadata] (summary)
    if (Array.isArray(questionData) && questionData.length >= 2) {
      const ratingsData = questionData[0];
      if (typeof ratingsData === 'object') {
        devLog('log', '📊 Format B detected: Array format with metadata');
        devLog('log', `📈 Metadata: ${questionData[1]}`, { fullArray: questionData });
        return ratingsData;
      }
    }
    
    devLog('warn', '⚠️ Unknown distribution format, returning empty object');
    return {};
  };
  
  // Detect survey structure type (Phase 1)
  const detectStructureType = (survey) => {
    devLog('log', '🔍 Detecting survey structure type...');
    
    // Check for new structure markers
    const hasNormalizedMap = survey.analysisData && survey.analysisData.normalized_label_map;
    const hasUsedMapping = survey.analysisData && survey.analysisData.used_mapping;
    
    devLog('log', '📊 Structure analysis:', {
      hasNormalizedMap: !!hasNormalizedMap,
      hasUsedMapping: !!hasUsedMapping,
      normalizedMapKeys: hasNormalizedMap ? Object.keys(survey.analysisData.normalized_label_map).length : 0,
      usedMappingKeys: hasUsedMapping ? Object.keys(survey.analysisData.used_mapping).length : 0
    });
    
    // NEW STRUCTURE: Has normalized_label_map (all new samples)
    if (hasNormalizedMap && hasUsedMapping) {
      devLog('log', '🆕 NEW STRUCTURE detected: Has normalized_label_map and used_mapping');
      return 'new_structure';
    }
    
    // OLD STRUCTURE: No normalized_label_map, need to check for mismatches
    devLog('log', '🔍 Checking for old structure type...');
    
    // For old structure detection, we need to extract some sample data first
    const distribution = survey.analysisData?.distribution;
    if (!distribution) {
      devLog('warn', '⚠️ No distribution data found, defaulting to old_compatible');
      return 'old_compatible';
    }
    
    // Get first question's data to check for mismatch
    const firstQuestion = Object.keys(distribution)[0];
    if (firstQuestion) {
      const questionData = distribution[firstQuestion];
      const ratingsData = extractDistributionData(questionData);
      const usedMapping = survey.analysisData.average_scores?.used_mapping;
      
      if (usedMapping && Object.keys(ratingsData).length > 0) {
        // Quick mismatch check
        const ratingsKeys = Object.keys(ratingsData);
        const mappingKeys = Object.keys(usedMapping);
        const matchingKeys = ratingsKeys.filter(key => mappingKeys.includes(key));
        
        if (matchingKeys.length === 0) {
          devLog('log', '🔄 OLD MISMATCH detected: No rating keys match mapping keys');
          devLog('log', '📊 Mismatch details:', { ratingsKeys, mappingKeys });
          return 'old_mismatch';
        }
      }
    }
    
    devLog('log', '✅ OLD COMPATIBLE detected: Keys match or no mapping conflicts');
    return 'old_compatible';
  };
  
  // Enhanced structure analysis for debugging (Phase 1)
  const analyzeStructureDetails = (survey) => {
    const structureType = detectStructureType(survey);
    
    const analysis = {
      structureType,
      hasDistribution: !!(survey.analysisData?.distribution),
      hasAverageScores: !!(survey.analysisData?.average_scores),
      hasNormalizedMap: !!(survey.analysisData?.normalized_label_map),
      hasUsedMapping: !!(survey.analysisData?.used_mapping)
    };
    
    if (survey.analysisData?.distribution) {
      const firstQuestion = Object.keys(survey.analysisData.distribution)[0];
      if (firstQuestion) {
        const questionData = survey.analysisData.distribution[firstQuestion];
        const ratingsData = extractDistributionData(questionData);
        analysis.sampleRatingsKeys = Object.keys(ratingsData);
        analysis.distributionFormat = Array.isArray(questionData) ? 'array' : 'object';
      }
    }
    
    if (survey.analysisData?.used_mapping) {
      analysis.usedMappingKeys = Object.keys(survey.analysisData.used_mapping);
    }
    
    if (survey.analysisData?.normalized_label_map) {
      analysis.normalizedMapEntries = Object.keys(survey.analysisData.normalized_label_map).length;
    }
    
    devLog('log', '📋 Complete structure analysis:', analysis);
    return analysis;
  };
  
  // Detect whether rating keys are labeled or already numeric
  const detectRatingKeyType = (ratingsObject, usedMapping) => {
    // VALIDATION FIRST - before any Object.keys() calls
    if (!ratingsObject || typeof ratingsObject !== 'object' || Array.isArray(ratingsObject)) {
      devLog('warn', '⚠️ Invalid ratings object');
      return 'unknown';
    }
    
    let ratingsKeys;
    try {
      ratingsKeys = Object.keys(ratingsObject);
    } catch (error) {
      devLog('error', '🚨 Failed to get object keys:', error);
      return 'unknown';
    }
    
    if (ratingsKeys.length === 0) {
      devLog('warn', '⚠️ No rating keys found');
      return 'empty';
    }
    
    devLog('log', '🔍 Detecting rating key type...', {
      ratingsKeys,
      mappingKeys: Object.keys(usedMapping || {}),
      ratingsCount: ratingsKeys.length
    });
    
    // Analyze the first few keys to determine type
    const sampleKeys = ratingsKeys.slice(0, 3); // Check first 3 keys for reliability
    let numericKeys = 0;
    let labeledKeys = 0;
    let mappableKeys = 0;
    
    sampleKeys.forEach(key => {
      // Check if key is purely numeric (like "1", "2", "3", "4", "5")
      if (/^\d+$/.test(key)) {
        numericKeys++;
        devLog('log', `📊 Numeric key detected: "${key}"`);
      }
      
      // Check if key exists in used_mapping (needs conversion)
      if (usedMapping && usedMapping.hasOwnProperty(key)) {
        labeledKeys++;
        mappableKeys++;
        devLog('log', `🏷️ Labeled key detected: "${key}" → ${usedMapping[key]}`);
      } else if (key.length > 1) {
        // Likely a label but not in mapping
        labeledKeys++;
        devLog('log', `🏷️ Potential label detected: "${key}" (not in mapping)`);
      }
    });
    
    devLog('log', '📊 Key analysis summary:', {
      sampleSize: sampleKeys.length,
      numericKeys,
      labeledKeys,
      mappableKeys,
      hasUsedMapping: !!usedMapping
    });
    
    // Determine key type based on analysis
    let keyType;
    if (numericKeys > 0 && labeledKeys === 0) {
      keyType = 'numeric';
      devLog('log', '✅ Keys are already numeric format');
    } else if (mappableKeys > 0 && usedMapping) {
      keyType = 'labeled';
      devLog('log', '✅ Keys are labeled and mappable');
    } else if (labeledKeys > 0 && !usedMapping) {
      keyType = 'labeled_unmappable';
      devLog('warn', '⚠️ Keys appear labeled but no mapping provided');
    } else if (numericKeys > 0 && labeledKeys > 0) {
      keyType = 'mixed';
      devLog('warn', '⚠️ Mixed key types detected');
    } else {
      keyType = 'unknown';
      devLog('warn', '⚠️ Unable to determine key type');
    }
    
    devLog('log', `🎯 Final key type determination: ${keyType}`);
    return keyType;
  };

  // TEST: Add testing function for detectRatingKeyType
  const testDetectRatingKeyType = () => {
    devLog('log', '🧪 Testing detectRatingKeyType function...');
    
    const testCases = [
      {
        name: 'Numeric Keys (English Format)',
        ratingsObject: {"1": 1, "2": 2, "3": 10, "4": 11, "5": 1},
        usedMapping: {"Strongly Disagree": 1, "Disagree": 2, "Neutral": 3, "Agree": 4, "Strongly Agree": 5},
        expected: 'numeric'
      },
      {
        name: 'Labeled Keys with Mapping (Chinese Format)',
        ratingsObject: {"3. 一般": 3, "4. 有些同意": 14, "5. 完全同意": 18},
        usedMapping: {"3. 一般": 3, "4. 有些同意": 4, "5. 完全同意": 5, "1. 完全不同意": 1, "2. 有些不同意": 2},
        expected: 'labeled'
      },
      {
        name: 'Labeled Keys without Mapping',
        ratingsObject: {"Strongly Agree": 5, "Agree": 10, "Neutral": 3},
        usedMapping: null,
        expected: 'labeled_unmappable'
      },
      {
        name: 'Mixed Keys',
        ratingsObject: {"1": 1, "Agree": 4, "3": 10},
        usedMapping: {"Agree": 4},
        expected: 'mixed'
      },
      {
        name: 'Empty Ratings Object',
        ratingsObject: {},
        usedMapping: {"3. 一般": 3},
        expected: 'empty'
      },
      {
        name: 'Invalid Input',
        ratingsObject: null,
        usedMapping: {},
        expected: 'unknown'
      },
      {
        name: 'Single Numeric Key',
        ratingsObject: {"5": 25},
        usedMapping: {},
        expected: 'numeric'
      },
      {
        name: 'Unmappable Labels',
        ratingsObject: {"Very Good": 5, "Good": 10},
        usedMapping: {"Excellent": 5, "Poor": 1},
        expected: 'labeled_unmappable'
      }
    ];
    
    testCases.forEach(testCase => {
      devLog('log', `🔬 Testing: ${testCase.name}`);
      const result = detectRatingKeyType(testCase.ratingsObject, testCase.usedMapping);
      const passed = result === testCase.expected;
      devLog(passed ? 'log' : 'error', `${passed ? '✅' : '❌'} Expected: ${testCase.expected}, Got: ${result}`);
    });
    
    devLog('log', '🏁 detectRatingKeyType testing complete');
  };

  // ===== DATA CONVERSION FUNCTIONS =====
  
  // Convert rating keys from labels to numeric format
  const convertRatingKeys = (ratingsObject, usedMapping, keyType) => {
    devLog('log', '🔄 Converting rating keys...', {
      keyType,
      originalKeys: Object.keys(ratingsObject || {}),
      hasMappingData: !!usedMapping
    });
    
    // Early validation
    if (!ratingsObject || typeof ratingsObject !== 'object' || Array.isArray(ratingsObject)) {
      devLog('warn', '⚠️ Invalid ratings object for conversion');
      return {};
    }
    
    // Handle different key types
    switch (keyType) {
      case 'numeric':
        // Already in correct format, return as-is
        devLog('log', '✅ Keys already numeric, no conversion needed');
        return ratingsObject;
        
      case 'labeled':
        // Convert labeled keys using mapping
        if (!usedMapping || typeof usedMapping !== 'object') {
          devLog('error', '🚨 Labeled keys detected but no valid mapping provided');
          return {};
        }
        
        const convertedRatings = {};
        let convertedCount = 0;
        let skippedCount = 0;
        
        Object.entries(ratingsObject).forEach(([label, count]) => {
          const numericValue = usedMapping[label];
          if (numericValue !== undefined && numericValue !== null) {
            // Convert to string key as expected by validation functions
            convertedRatings[String(numericValue)] = count;
            convertedCount++;
            devLog('log', `🔄 Converted: "${label}" → "${numericValue}" (${count} responses)`);
          } else {
            skippedCount++;
            devLog('warn', `⚠️ Skipped unmappable label: "${label}" (${count} responses)`);
          }
        });
        
        devLog('log', '✅ Label conversion complete:', {
          convertedKeys: convertedCount,
          skippedKeys: skippedCount,
          finalKeys: Object.keys(convertedRatings)
        });
        
        return convertedRatings;
        
      case 'labeled_unmappable':
        devLog('warn', '⚠️ Cannot convert labeled keys without mapping');
        return {};
        
      case 'mixed':
        // Handle mixed keys - convert what we can, keep numeric ones
        if (!usedMapping) {
          devLog('warn', '⚠️ Mixed keys detected but no mapping for conversion');
          return ratingsObject; // Return as-is, hope for the best
        }
        
        const mixedConverted = {};
        let numericKept = 0;
        let labelsConverted = 0;
        
        Object.entries(ratingsObject).forEach(([key, count]) => {
          // Check if it's already numeric
          if (/^\d+$/.test(key)) {
            mixedConverted[key] = count;
            numericKept++;
            devLog('log', `📊 Kept numeric key: "${key}"`);
          } else {
            // Try to convert label
            const numericValue = usedMapping[key];
            if (numericValue !== undefined) {
              mixedConverted[String(numericValue)] = count;
              labelsConverted++;
              devLog('log', `🔄 Converted mixed key: "${key}" → "${numericValue}"`);
            } else {
              devLog('warn', `⚠️ Skipped unmappable mixed key: "${key}"`);
            }
          }
        });
        
        devLog('log', '✅ Mixed conversion complete:', {
          numericKept,
          labelsConverted,
          finalKeys: Object.keys(mixedConverted)
        });
        
        return mixedConverted;
        
      case 'empty':
        devLog('warn', '⚠️ Empty ratings object');
        return {};
        
      case 'unknown':
      default:
        devLog('warn', '⚠️ Unknown key type, returning original object');
        return ratingsObject;
    }
  };

  // TEST: Add testing function for convertRatingKeys
  const testConvertRatingKeys = () => {
    devLog('log', '🧪 Testing convertRatingKeys function...');
    
    const testCases = [
      {
        name: 'Numeric Keys (No Conversion Needed)',
        ratingsObject: {"1": 1, "2": 2, "3": 10, "4": 11, "5": 1},
        usedMapping: {"Strongly Disagree": 1, "Disagree": 2, "Neutral": 3, "Agree": 4, "Strongly Agree": 5},
        keyType: 'numeric',
        expected: {"1": 1, "2": 2, "3": 10, "4": 11, "5": 1}
      },
      {
        name: 'Chinese Labeled Keys',
        ratingsObject: {"3. 一般": 3, "4. 有些同意": 14, "5. 完全同意": 18},
        usedMapping: {"3. 一般": 3, "4. 有些同意": 4, "5. 完全同意": 5, "1. 完全不同意": 1, "2. 有些不同意": 2},
        keyType: 'labeled',
        expected: {"3": 3, "4": 14, "5": 18}
      },
      {
        name: 'English Labeled Keys',
        ratingsObject: {"Strongly Agree": 5, "Agree": 10, "Neutral": 3},
        usedMapping: {"Strongly Agree": 5, "Agree": 4, "Neutral": 3, "Disagree": 2, "Strongly Disagree": 1},
        keyType: 'labeled',
        expected: {"5": 5, "4": 10, "3": 3}
      },
      {
        name: 'Mixed Keys',
        ratingsObject: {"1": 1, "Agree": 4, "3": 10},
        usedMapping: {"Agree": 4, "Disagree": 2},
        keyType: 'mixed',
        expected: {"1": 1, "4": 4, "3": 10}
      },
      {
        name: 'Labeled Without Mapping',
        ratingsObject: {"Excellent": 5, "Good": 10},
        usedMapping: null,
        keyType: 'labeled_unmappable',
        expected: {}
      },
      {
        name: 'Empty Object',
        ratingsObject: {},
        usedMapping: {"Good": 4},
        keyType: 'empty',
        expected: {}
      },
      {
        name: 'Invalid Input',
        ratingsObject: null,
        usedMapping: {},
        keyType: 'unknown',
        expected: {}
      },
      {
        name: 'Partial Mapping Coverage',
        ratingsObject: {"Good": 10, "Bad": 5, "Unknown": 2},
        usedMapping: {"Good": 4, "Bad": 2}, // Missing "Unknown"
        keyType: 'labeled',
        expected: {"4": 10, "2": 5}
      }
    ];
    
    testCases.forEach(testCase => {
      devLog('log', `🔬 Testing: ${testCase.name}`);
      const result = convertRatingKeys(testCase.ratingsObject, testCase.usedMapping, testCase.keyType);
      
      // Deep comparison for objects
      const resultKeys = Object.keys(result).sort();
      const expectedKeys = Object.keys(testCase.expected).sort();
      const keysMatch = JSON.stringify(resultKeys) === JSON.stringify(expectedKeys);
      
      let valuesMatch = true;
      if (keysMatch) {
        valuesMatch = resultKeys.every(key => result[key] === testCase.expected[key]);
      }
      
      const passed = keysMatch && valuesMatch;
      devLog(passed ? 'log' : 'error', `${passed ? '✅' : '❌'} Test result:`, {
        expected: testCase.expected,
        actual: result,
        keysMatch,
        valuesMatch
      });
    });
    
    devLog('log', '🏁 convertRatingKeys testing complete');
  };

  // ===== QUESTION PROCESSING FUNCTIONS =====
  
  // Orchestrate the complete question processing pipeline
  const processQuestion = (questionText, questionData, averageData, usedMapping, subFormat) => {
    devLog('log', `🔄 Processing question: "${questionText.substring(0, 50)}..."`, {
      subFormat,
      hasAverageData: !!averageData,
      hasUsedMapping: !!usedMapping
    });
    
    let processedQuestion = {
      question_text: questionText,
      response_count: 0,
      average_rating: 0,
      ratings_breakdown: {},
      processing_status: 'failed'
    };
    
    try {
      // Step 1: Extract raw ratings data based on sub-format
      devLog('log', '📊 Step 1: Extracting ratings data...');
      const rawRatings = extractRatingsData(questionData, subFormat);
      
      if (!rawRatings || Object.keys(rawRatings).length === 0) {
        devLog('warn', '⚠️ No ratings data extracted, skipping question');
        return {
          ...processedQuestion,
          processing_status: 'no_data'
        };
      }
      
      // Step 2: Validate used mapping (Step 1 implementation)
      devLog('log', '🔍 Step 2a: Validating used mapping...');
      const mappingValidation = validateUsedMapping(rawRatings, usedMapping);
      devLog('log', `📋 Mapping validation result: ${mappingValidation.status}`, mappingValidation);
      
      // Step 2b: Smart adaptation for mismatched keys (Step 2 implementation)
      let adaptedRatings = rawRatings;
      if (mappingValidation.status === 'no_matching_keys' || mappingValidation.status === 'partial_match') {
        devLog('log', '🔍 Step 2b: Smart adaptation - detecting mismatch patterns...');
        const mismatchPattern = detectMismatchPattern(
          mappingValidation.ratingsKeys, 
          mappingValidation.mappingKeys, 
          usedMapping
        );
        devLog('log', `🎯 Mismatch pattern detected: ${mismatchPattern.type}`, mismatchPattern);
        
        if (mismatchPattern.type !== 'unrecognized' && mismatchPattern.type !== 'no_mismatch') {
          const convertedRatings = smartConvertRatings(rawRatings, mismatchPattern);
          if (convertedRatings) {
            adaptedRatings = convertedRatings;
            devLog('log', '✅ Smart adaptation successful - using converted ratings');
          } else {
            devLog('warn', '⚠️ Smart adaptation failed - falling back to original logic');
          }
        }
      } else {
        devLog('log', '✅ No smart adaptation needed - keys match mapping');
      }
      
      // Step 2c: Detect what type of keys we're working with (existing logic fallback)
      devLog('log', '🔍 Step 2c: Detecting rating key type (fallback)...');
      const keyType = detectRatingKeyType(adaptedRatings, usedMapping);
      
      if (keyType === 'unknown' || keyType === 'empty') {
        devLog('warn', `⚠️ Cannot process question with key type: ${keyType}`);
        return {
          ...processedQuestion,
          processing_status: 'invalid_keys'
        };
      }
      
      // Step 3: Convert keys to numeric format if needed
      devLog('log', '🔄 Step 3: Converting rating keys...');
      const convertedRatings = convertRatingKeys(adaptedRatings, usedMapping, keyType);
      
      if (!convertedRatings || Object.keys(convertedRatings).length === 0) {
        devLog('warn', '⚠️ Key conversion failed, no valid ratings');
        return {
          ...processedQuestion,
          processing_status: 'conversion_failed'
        };
      }
      
      // Step 4: Extract response count and average from averageData
      devLog('log', '📊 Step 4: Extracting metadata...');
      const responseCount = averageData?.total_count || 0;
      const averageRating = averageData?.average || 0;
      
      // Step 5: Build final standardized question object
      processedQuestion = {
        question_text: questionText,
        response_count: responseCount,
        average_rating: averageRating,
        ratings_breakdown: convertedRatings,
        processing_status: 'success',
        metadata: {
          original_key_type: keyType,
          conversion_applied: keyType === 'labeled' || keyType === 'mixed',
          ratings_count: Object.keys(convertedRatings).length,
          total_responses: Object.values(convertedRatings).reduce((sum, count) => sum + count, 0)
        }
      };
      
      devLog('log', '✅ Question processing complete:', {
        questionLength: questionText.length,
        responseCount,
        averageRating,
        ratingsKeys: Object.keys(convertedRatings),
        totalResponses: processedQuestion.metadata.total_responses
      });
      
      return processedQuestion;
      
    } catch (error) {
      devLog('error', '🚨 Error processing question:', error);
      return {
        ...processedQuestion,
        processing_status: 'error',
        error_message: error.message
      };
    }
  };

  // TEST: Add testing function for processQuestion
  const testProcessQuestion = () => {
    devLog('log', '🧪 Testing processQuestion function...');
    
    const testCases = [
      {
        name: 'Chinese Format Question',
        questionText: '【我掌握了金融知识与技能】',
        questionData: {
          "3. 一般": 3,
          "4. 有些同意": 14,
          "5. 完全同意": 18
        },
        averageData: {
          average: 4.47,
          total_count: 35,
          total_score: 143
        },
        usedMapping: {
          "3. 一般": 3,
          "4. 有些同意": 4,
          "5. 完全同意": 5,
          "1. 完全不同意": 1,
          "2. 有些不同意": 2
        },
        subFormat: 'object_structure',
        expectedStatus: 'success'
      },
      {
        name: 'English Format Question',
        questionText: 'Do you like the new cartridge?',
        questionData: [
          {"1": 1, "2": 2, "3": 10, "4": 11, "5": 1},
          1699
        ],
        averageData: {
          average: 3.36,
          total_count: 25,
          total_score: 84
        },
        usedMapping: {
          "Strongly Disagree": 1,
          "Disagree": 2,
          "Neutral": 3,
          "Agree": 4,
          "Strongly Agree": 5
        },
        subFormat: 'array_structure',
        expectedStatus: 'success'
      },
      {
        name: 'Empty Question Data',
        questionText: 'Empty question',
        questionData: {},
        averageData: null,
        usedMapping: {},
        subFormat: 'object_structure',
        expectedStatus: 'no_data'
      },
      {
        name: 'Invalid Question Data',
        questionText: 'Invalid question',
        questionData: null,
        averageData: {average: 3.0, total_count: 10},
        usedMapping: {"Good": 4},
        subFormat: 'object_structure',
        expectedStatus: 'no_data'
      },
      {
        name: 'Unmappable Labels',
        questionText: 'Unmappable question',
        questionData: {"Unknown Label": 5},
        averageData: {average: 3.0, total_count: 5},
        usedMapping: {"Different Label": 3},
        subFormat: 'object_structure',
        expectedStatus: 'conversion_failed'
      }
    ];
    
    testCases.forEach(testCase => {
      devLog('log', `🔬 Testing: ${testCase.name}`);
      const result = processQuestion(
        testCase.questionText,
        testCase.questionData,
        testCase.averageData,
        testCase.usedMapping,
        testCase.subFormat
      );
      
      const statusMatch = result.processing_status === testCase.expectedStatus;
      const hasValidStructure = result.question_text && typeof result.response_count === 'number';
      
      const passed = statusMatch && hasValidStructure;
      devLog(passed ? 'log' : 'error', `${passed ? '✅' : '❌'} Test result:`, {
        expectedStatus: testCase.expectedStatus,
        actualStatus: result.processing_status,
        hasValidStructure,
        questionText: result.question_text?.substring(0, 30) + '...',
        responseCount: result.response_count,
        ratingsKeys: Object.keys(result.ratings_breakdown || {})
      });
    });
    
    devLog('log', '🏁 processQuestion testing complete');
  };

  // TEST: Add integration test for complete adaptSurveyData pipeline
  const testCompleteIntegration = () => {
    devLog('log', '🧪 Testing complete integration pipeline...');
    
    const testCases = [
      {
        name: 'Chinese Format Complete Data',
        analysisData: {
          distribution: {
            "【我掌握了金融知识与技能】": {
              "3. 一般": 3,
              "4. 有些同意": 14,
              "5. 完全同意": 18
            },
            "【我清晰了解中国独特的营商环境】": {
              "3. 一般": 8,
              "4. 有些同意": 17,
              "5. 完全同意": 6,
              "2. 有些不同意": 1
            }
          },
          average_scores: {
            averages: {
              "【我掌握了金融知识与技能】": {
                average: 4.47,
                total_count: 35,
                total_score: 143
              },
              "【我清晰了解中国独特的营商环境】": {
                average: 3.89,
                total_count: 32,
                total_score: 124
              }
            },
            used_mapping: {
              "3. 一般": 3,
              "4. 有些同意": 4,
              "5. 完全同意": 5,
              "1. 完全不同意": 1,
              "2. 有些不同意": 2
            },
            overall_average: 4.18
          }
        },
        expectedQuestions: 2,
        expectedFormat: 'new'
      },
      {
        name: 'English Format Complete Data',
        analysisData: {
          distribution: {
            "Do you like the new cartridge?": [
              {"1": 1, "2": 2, "3": 10, "4": 11, "5": 1},
              1699
            ],
            "Are you satisfied with the service?": [
              {"1": 0, "2": 1, "3": 1, "4": 20, "5": 3},
              1699
            ]
          },
          average_scores: {
            averages: {
              "Do you like the new cartridge?": {
                average: 3.36,
                total_count: 25,
                total_score: 84
              },
              "Are you satisfied with the service?": {
                average: 4.0,
                total_count: 25,
                total_score: 100
              }
            },
            used_mapping: {
              "Strongly Disagree": 1,
              "Disagree": 2,
              "Neutral": 3,
              "Agree": 4,
              "Strongly Agree": 5
            },
            overall_average: 3.68
          }
        },
        expectedQuestions: 2,
        expectedFormat: 'new'
      },
      {
        name: 'Old Format Data',
        analysisData: {
          questions: [
            {
              question_text: "Test Question",
              response_count: 25,
              average_rating: 3.5,
              ratings_breakdown: {"1": 1, "2": 2, "3": 10, "4": 11, "5": 1}
            }
          ],
          overall_summary: {
            total_responses: 25,
            total_questions: 1,
            overall_average: 3.5
          }
        },
        expectedQuestions: 1,
        expectedFormat: 'old'
      }
    ];
    
    testCases.forEach(testCase => {
      devLog('log', `🔬 Integration Testing: ${testCase.name}`);
      const result = adaptSurveyData(testCase.analysisData);
      
      const hasQuestions = result.questions && Array.isArray(result.questions);
      const correctQuestionCount = hasQuestions && result.questions.length === testCase.expectedQuestions;
      const hasOverallSummary = result.overall_summary && typeof result.overall_summary.overall_average === 'number';
      
      let hasValidRatingsBreakdown = false;
      if (hasQuestions && result.questions.length > 0) {
        const firstQuestion = result.questions[0];
        hasValidRatingsBreakdown = firstQuestion.ratings_breakdown && 
                                   Object.keys(firstQuestion.ratings_breakdown).length > 0;
      }
      
      const passed = hasQuestions && correctQuestionCount && hasOverallSummary && hasValidRatingsBreakdown;
      devLog(passed ? 'log' : 'error', `${passed ? '✅' : '❌'} Integration test result:`, {
        expectedQuestions: testCase.expectedQuestions,
        actualQuestions: hasQuestions ? result.questions.length : 0,
        hasOverallSummary,
        hasValidRatingsBreakdown,
        sampleRatingsKeys: hasQuestions && result.questions.length > 0 ? 
                          Object.keys(result.questions[0].ratings_breakdown || {}) : []
      });
    });
    
    devLog('log', '🏁 Complete integration testing complete');
  };

  // ===== COMPONENT LIFECYCLE EFFECTS =====

  // Test Phase 1 structure detection (Phase 1)
  const testStructureDetection = () => {
    devLog('log', '🧪 Testing Phase 1: Structure Detection...');
    
    // Test Case 1: New structure (like your samples)
    const newStructureSample = {
      analysisData: {
        distribution: {
          "Test Question": {"3. 一般": 3, "4. 有些同意": 14, "5. 完全同意": 18}
        },
        normalized_label_map: {"3. 一般": "Neutral", "4. 有些同意": "Agree", "5. 完全同意": "Strongly Agree"},
        used_mapping: {"Agree": 4, "Neutral": 3, "Strongly Agree": 5}
      }
    };
    
    // Test Case 2: Old mismatch structure (letter grades)
    const oldMismatchSample = {
      analysisData: {
        distribution: {
          "Test Question": {"A": 3, "B": 20, "C": 1, "D": 1, "E": 0}
        },
        average_scores: {
          used_mapping: {"Strongly Agree": 5, "Agree": 4, "Neutral": 3, "Disagree": 2, "Strongly Disagree": 1}
        }
      }
    };
    
    // Test Case 3: Old compatible structure
    const oldCompatibleSample = {
      analysisData: {
        distribution: {
          "Test Question": {"Strongly Agree": 5, "Agree": 10, "Neutral": 3}
        },
        average_scores: {
          used_mapping: {"Strongly Agree": 5, "Agree": 4, "Neutral": 3}
        }
      }
    };
    
    devLog('log', '🔬 Testing New Structure Detection...');
    const newResult = analyzeStructureDetails(newStructureSample);
    devLog('log', `✅ Expected: new_structure, Got: ${newResult.structureType}`);
    
    devLog('log', '🔬 Testing Old Mismatch Detection...');
    const oldMismatchResult = analyzeStructureDetails(oldMismatchSample);
    devLog('log', `✅ Expected: old_mismatch, Got: ${oldMismatchResult.structureType}`);
    
    devLog('log', '🔬 Testing Old Compatible Detection...');
    const oldCompatibleResult = analyzeStructureDetails(oldCompatibleSample);
    devLog('log', `✅ Expected: old_compatible, Got: ${oldCompatibleResult.structureType}`);
    
    devLog('log', '🏁 Phase 1 structure detection testing complete');
  };

  // Run all tests after component mounts (React best practice)
  React.useEffect(() => {
    if (isDevelopment) {
      devLog('log', '🧪 Starting development testing suite...');
      testDetectNewFormatSubType();
      testExtractRatingsData();
      testDetectRatingKeyType();
      testConvertRatingKeys();
      testProcessQuestion();
      testCompleteIntegration();
      testStructureDetection(); // Phase 1 test
      devLog('log', '🏁 Development testing suite complete');
    }
  }, []);

  // ===========================
  // Universal Survey Processor (Enhanced Phase 2)
  // ===========================

  /**
   * Detect what type of conversion is needed for distribution keys
   * @param {Array} distributionKeys - Keys from the distribution data
   * @param {Object} context - Context with mappings and format info
   * @returns {Object} - Conversion plan with type and details
   */
  function detectConversionNeeded(distributionKeys, context) {
    devLog('log', '🔍 Detecting conversion type needed...', {
      distributionKeys: distributionKeys.slice(0, 5), // Show first 5 keys
      distributionKeysCount: distributionKeys.length,
      hasNormalizedMapping: !!context.normalizedLabelMap,
      hasUsedMapping: !!context.usedMapping
    });

    // Case 1: Already in correct numeric format (1, 2, 3, 4, 5)
    if (distributionKeys.every(key => /^\d+$/.test(key))) {
      devLog('log', '✅ Detection result: Already numeric format - no conversion needed');
      return { 
        type: 'no_conversion', 
        reason: 'already_numeric',
        originalKeys: distributionKeys,
        targetKeys: distributionKeys
      };
    }

    // Case 2: Two-step conversion needed (Localized → English → Numeric)
    if (context.normalizedLabelMap && hasNormalizedLabelMapping(distributionKeys, context.normalizedLabelMap)) {
      devLog('log', '✅ Detection result: Two-step conversion needed (localized → English → numeric)');
      return { 
        type: 'two_step', 
        reason: 'localized_to_numeric',
        originalKeys: distributionKeys,
        intermediateMapping: context.normalizedLabelMap,
        finalMapping: context.usedMapping
      };
    }

    // Case 3: One-step conversion needed (English labels → Numeric)
    if (context.usedMapping && hasDirectUsedMapping(distributionKeys, context.usedMapping)) {
      devLog('log', '✅ Detection result: One-step conversion needed (English labels → numeric)');
      return { 
        type: 'one_step', 
        reason: 'english_to_numeric',
        originalKeys: distributionKeys,
        directMapping: context.usedMapping
      };
    }

    // Case 4: TODO - Non-standard mappings (future implementation)
    // if (hasNonStandardMapping(distributionKeys, context)) {
    //   return { type: 'non_standard', reason: 'custom_scale_conversion' };
    // }

    // Case 5: Unsupported format - fallback to old processor
    devLog('warn', '⚠️ Detection result: Unsupported format - will fallback to old processor');
    return { 
      type: 'fallback', 
      reason: 'unsupported_format',
      originalKeys: distributionKeys,
      suggestions: 'Consider adding normalized_label_map or ensuring used_mapping compatibility'
    };
  }

  /**
   * Check if distribution keys can be mapped using normalized_label_map
   * @param {Array} distributionKeys - Keys from distribution
   * @param {Object} normalizedLabelMap - The normalized label mapping
   * @returns {boolean} - True if mapping is possible
   */
  function hasNormalizedLabelMapping(distributionKeys, normalizedLabelMap) {
    if (!normalizedLabelMap || typeof normalizedLabelMap !== 'object') {
      return false;
    }

    const mappingKeys = Object.keys(normalizedLabelMap);
    const matchingKeys = distributionKeys.filter(key => mappingKeys.includes(key));
    
    devLog('log', '🔍 Checking normalized label mapping compatibility:', {
      distributionKeysCount: distributionKeys.length,
      mappingKeysCount: mappingKeys.length,
      matchingKeysCount: matchingKeys.length,
      matchPercentage: Math.round((matchingKeys.length / distributionKeys.length) * 100)
    });

    // Require at least 50% of distribution keys to have mappings
    return matchingKeys.length >= Math.ceil(distributionKeys.length * 0.5);
  }

  /**
   * Check if distribution keys can be directly mapped using used_mapping
   * @param {Array} distributionKeys - Keys from distribution  
   * @param {Object} usedMapping - The used mapping object
   * @returns {boolean} - True if direct mapping is possible
   */
  function hasDirectUsedMapping(distributionKeys, usedMapping) {
    if (!usedMapping || typeof usedMapping !== 'object') {
      return false;
    }

    const mappingKeys = Object.keys(usedMapping);
    const matchingKeys = distributionKeys.filter(key => mappingKeys.includes(key));
    
    devLog('log', '🔍 Checking direct used mapping compatibility:', {
      distributionKeysCount: distributionKeys.length,
      usedMappingKeysCount: mappingKeys.length,
      matchingKeysCount: matchingKeys.length,
      matchPercentage: Math.round((matchingKeys.length / distributionKeys.length) * 100)
    });

    // Require at least 50% of distribution keys to have mappings
    return matchingKeys.length >= Math.ceil(distributionKeys.length * 0.5);
  }

  /**
   * Detect the scale range from used_mapping
   * @param {Object} usedMapping - The used mapping object
   * @returns {Object} - Scale information
   */
  function detectScale(usedMapping) {
    if (!usedMapping || typeof usedMapping !== 'object') {
      // Default to 1-5 scale
      return { scale: ['1', '2', '3', '4', '5'], min: 1, max: 5, isStandard: true };
    }

    const values = Object.values(usedMapping).filter(val => typeof val === 'number');
    
    if (values.length === 0) {
      return { scale: ['1', '2', '3', '4', '5'], min: 1, max: 5, isStandard: true };
    }

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const scale = [];
    
    for (let i = minValue; i <= maxValue; i++) {
      scale.push(String(i));
    }
    
    const isStandard = minValue === 1 && maxValue === 5;
    
    devLog('log', '📊 Detected scale information:', {
      minValue,
      maxValue,
      scaleLength: scale.length,
      scale,
      isStandard
    });

    return { scale, min: minValue, max: maxValue, isStandard };
  }

  /**
   * Universal conversion dispatcher - routes to appropriate conversion function
   * @param {Object} distribution - The distribution data to convert
   * @param {Object} conversionPlan - Plan from detectConversionNeeded()
   * @returns {Object} - Converted distribution with validation info
   */
  function universalConvertDistribution(distribution, conversionPlan) {
    devLog('log', '🔄 Starting universal conversion...', {
      conversionType: conversionPlan.type,
      reason: conversionPlan.reason,
      originalKeysCount: Object.keys(distribution).length
    });

    switch (conversionPlan.type) {
      case 'no_conversion':
        return handleNoConversion(distribution, conversionPlan);
      
      case 'two_step':
        return handleTwoStepConversion(distribution, conversionPlan);
      
      case 'one_step':
        return handleOneStepConversion(distribution, conversionPlan);
      
      case 'fallback':
        return handleFallbackConversion(distribution, conversionPlan);
      
      default:
        devLog('error', '❌ Unknown conversion type:', conversionPlan.type);
        return {
          success: false,
          converted: {},
          error: `Unknown conversion type: ${conversionPlan.type}`,
          validation: { errors: [`Unsupported conversion type: ${conversionPlan.type}`] }
        };
    }
  }

  /**
   * Handle no conversion needed (already numeric)
   * @param {Object} distribution - The distribution data
   * @param {Object} conversionPlan - The conversion plan
   * @returns {Object} - Conversion result
   */
  function handleNoConversion(distribution, conversionPlan) {
    devLog('log', '✅ No conversion needed - distribution already in numeric format');
    
    return {
      success: true,
      converted: distribution,
      conversionType: 'no_conversion',
      validation: {
        errors: [],
        warnings: [],
        info: ['Distribution already in correct numeric format']
      }
    };
  }

  /**
   * Handle two-step conversion (Localized → English → Numeric)
   * @param {Object} distribution - The distribution data
   * @param {Object} conversionPlan - The conversion plan
   * @returns {Object} - Conversion result
   */
  function handleTwoStepConversion(distribution, conversionPlan) {
    devLog('log', '🔄 Starting two-step conversion (localized → English → numeric)...');
    
    const step1Result = convertLocalizedToEnglish(distribution, conversionPlan.intermediateMapping);
    if (!step1Result.success) {
      return step1Result;
    }

    devLog('log', '✅ Step 1 complete - converted to English labels:', {
      step1KeysCount: Object.keys(step1Result.converted).length,
      step1Keys: Object.keys(step1Result.converted).slice(0, 5)
    });

    const step2Result = convertEnglishToNumeric(step1Result.converted, conversionPlan.finalMapping);
    if (!step2Result.success) {
      return step2Result;
    }

    devLog('log', '✅ Step 2 complete - converted to numeric:', {
      finalKeysCount: Object.keys(step2Result.converted).length,
      finalKeys: Object.keys(step2Result.converted)
    });

    return {
      success: true,
      converted: step2Result.converted,
      conversionType: 'two_step',
      validation: {
        errors: [...step1Result.validation.errors, ...step2Result.validation.errors],
        warnings: [...step1Result.validation.warnings, ...step2Result.validation.warnings],
        info: [
          'Two-step conversion completed successfully',
          `Step 1: ${step1Result.validation.info.join(', ')}`,
          `Step 2: ${step2Result.validation.info.join(', ')}`
        ]
      }
    };
  }

  /**
   * Handle one-step conversion (English → Numeric)
   * @param {Object} distribution - The distribution data
   * @param {Object} conversionPlan - The conversion plan
   * @returns {Object} - Conversion result
   */
  function handleOneStepConversion(distribution, conversionPlan) {
    devLog('log', '🔄 Starting one-step conversion (English → numeric)...');
    
    const result = convertEnglishToNumeric(distribution, conversionPlan.directMapping);
    
    if (result.success) {
      devLog('log', '✅ One-step conversion completed successfully:', {
        finalKeysCount: Object.keys(result.converted).length,
        finalKeys: Object.keys(result.converted)
      });
    }

    return {
      ...result,
      conversionType: 'one_step'
    };
  }

  /**
   * Handle fallback conversion (return original with warnings)
   * @param {Object} distribution - The distribution data
   * @param {Object} conversionPlan - The conversion plan
   * @returns {Object} - Conversion result
   */
  function handleFallbackConversion(distribution, conversionPlan) {
    devLog('warn', '⚠️ Using fallback conversion - returning original distribution');
    
    return {
      success: true,
      converted: distribution,
      conversionType: 'fallback',
      validation: {
        errors: [],
        warnings: [
          'No suitable conversion mapping found',
          'Returning original distribution format',
          conversionPlan.suggestions || 'Consider adding appropriate label mappings'
        ],
        info: ['Fallback conversion used - original format preserved']
      }
    };
  }

  /**
   * Convert localized keys to English labels using normalized_label_map
   * @param {Object} distribution - Distribution with localized keys
   * @param {Object} normalizedLabelMap - Mapping from localized to English
   * @returns {Object} - Conversion result
   */
  function convertLocalizedToEnglish(distribution, normalizedLabelMap) {
    devLog('log', '🔄 Converting localized keys to English labels...', {
      distributionKeys: Object.keys(distribution),
      normalizedLabelMapKeys: Object.keys(normalizedLabelMap)
    });

    const converted = {};
    const validation = { errors: [], warnings: [], info: [] };
    let convertedCount = 0;
    let missingCount = 0;

    for (const [localizedKey, count] of Object.entries(distribution)) {
      if (normalizedLabelMap[localizedKey]) {
        const englishLabel = normalizedLabelMap[localizedKey];
        converted[englishLabel] = count;
        convertedCount++;
        
        devLog('log', `  ✅ ${localizedKey} → ${englishLabel} (${count})`);
      } else {
        // Missing mapping - fill with zero (permissive approach)
        devLog('warn', `  ⚠️ No mapping found for key: ${localizedKey} (count: ${count}) - will skip this entry`);
        validation.warnings.push(`No English mapping found for localized key: ${localizedKey}`);
        missingCount++;
      }
    }

    // Log conversion summary
    devLog('log', '📊 Localized → English conversion summary:', {
      totalKeys: Object.keys(distribution).length,
      convertedCount,
      missingCount,
      conversionRate: Math.round((convertedCount / Object.keys(distribution).length) * 100)
    });

    validation.info.push(`Converted ${convertedCount}/${Object.keys(distribution).length} localized keys to English`);
    
    if (missingCount > 0) {
      validation.warnings.push(`${missingCount} keys had no English mapping and were skipped`);
    }

    return {
      success: convertedCount > 0,
      converted,
      validation
    };
  }

  /**
   * Convert English labels to numeric strings using used_mapping
   * @param {Object} distribution - Distribution with English label keys
   * @param {Object} usedMapping - Mapping from English to numeric values
   * @returns {Object} - Conversion result
   */
  function convertEnglishToNumeric(distribution, usedMapping) {
    devLog('log', '🔄 Converting English labels to numeric strings...', {
      distributionKeys: Object.keys(distribution),
      usedMappingKeys: usedMapping ? Object.keys(usedMapping) : []
    });

    if (!usedMapping || typeof usedMapping !== 'object') {
      devLog('error', '❌ No valid used_mapping provided for English → numeric conversion');
      return {
        success: false,
        converted: {},
        validation: {
          errors: ['No valid used_mapping provided for English to numeric conversion'],
          warnings: [],
          info: []
        }
      };
    }

    const converted = {};
    const validation = { errors: [], warnings: [], info: [] };
    const scaleInfo = detectScale(usedMapping);
    let convertedCount = 0;
    let missingCount = 0;

    // Fill all scale positions with zero first (permissive approach)
    scaleInfo.scale.forEach(numericKey => {
      converted[numericKey] = 0;
    });

    for (const [englishLabel, count] of Object.entries(distribution)) {
      if (usedMapping[englishLabel] !== undefined) {
        const numericValue = usedMapping[englishLabel];
        const numericKey = String(numericValue);
        converted[numericKey] = count;
        convertedCount++;
        
        devLog('log', `  ✅ ${englishLabel} → ${numericKey} (${count})`);
      } else {
        devLog('warn', `  ⚠️ No numeric mapping found for English label: ${englishLabel} (count: ${count})`);
        validation.warnings.push(`No numeric mapping found for English label: ${englishLabel}`);
        missingCount++;
      }
    }

    // Log conversion summary
    devLog('log', '📊 English → Numeric conversion summary:', {
      totalKeys: Object.keys(distribution).length,
      convertedCount,
      missingCount,
      scaleRange: `${scaleInfo.min}-${scaleInfo.max}`,
      finalDistribution: converted
    });

    validation.info.push(`Converted ${convertedCount}/${Object.keys(distribution).length} English labels to numeric`);
    validation.info.push(`Filled missing scale positions with zero (${scaleInfo.scale.length} total positions)`);
    
    if (missingCount > 0) {
      validation.warnings.push(`${missingCount} English labels had no numeric mapping`);
    }

    return {
      success: convertedCount > 0,
      converted,
      validation
    };
  }

  /**
   * Comprehensive validation system for survey data and conversions
   * @param {Object} originalData - Original survey data
   * @param {Object} conversionResult - Result from universal conversion
   * @param {Object} context - Context with mappings and metadata
   * @returns {Object} - Detailed validation report
   */
  function validateConversionResult(originalData, conversionResult, context) {
    devLog('log', '🔍 Starting comprehensive validation...', {
      hasOriginalData: !!originalData,
      hasConversionResult: !!conversionResult,
      conversionType: conversionResult?.conversionType
    });

    const validation = {
      isValid: true,
      errors: [],
      warnings: [],
      info: [],
      metrics: {},
      recommendations: []
    };

    // 1. Basic Structure Validation
    validateBasicStructure(originalData, conversionResult, validation);
    
    // 2. Data Integrity Validation
    validateDataIntegrity(originalData, conversionResult, validation);
    
    // 3. Scale Consistency Validation
    validateScaleConsistency(conversionResult, context, validation);
    
    // 4. Response Count Validation
    validateResponseCounts(originalData, conversionResult, validation);
    
    // 5. Mapping Quality Validation
    validateMappingQuality(conversionResult, context, validation);

    // 6. Performance Metrics
    calculatePerformanceMetrics(originalData, conversionResult, validation);

    // 7. Generate Recommendations
    generateRecommendations(validation);

    // Final validation status
    validation.isValid = validation.errors.length === 0;

    devLog('log', '📋 Validation Summary:', {
      isValid: validation.isValid,
      errorsCount: validation.errors.length,
      warningsCount: validation.warnings.length,
      infoCount: validation.info.length,
      metrics: validation.metrics
    });

    if (validation.errors.length > 0) {
      devLog('error', '❌ Validation Errors Found:', validation.errors);
    }

    if (validation.warnings.length > 0) {
      devLog('warn', '⚠️ Validation Warnings:', validation.warnings);
    }

    return validation;
  }

  /**
   * Validate basic data structure requirements
   */
  function validateBasicStructure(originalData, conversionResult, validation) {
    devLog('log', '🔍 Validating basic structure...');

    // Check conversion result structure
    if (!conversionResult) {
      validation.errors.push('Conversion result is null or undefined');
      return;
    }

    if (typeof conversionResult !== 'object') {
      validation.errors.push('Conversion result is not an object');
      return;
    }

    if (!conversionResult.hasOwnProperty('success')) {
      validation.errors.push('Conversion result missing success property');
    }

    if (!conversionResult.hasOwnProperty('converted')) {
      validation.errors.push('Conversion result missing converted property');
    }

    if (!conversionResult.hasOwnProperty('validation')) {
      validation.errors.push('Conversion result missing validation property');
    }

    // Check converted data structure
    if (conversionResult.converted && typeof conversionResult.converted !== 'object') {
      validation.errors.push('Converted data is not an object');
    }

    validation.info.push('Basic structure validation completed');
  }

  /**
   * Validate data integrity (totals, non-negative values, etc.)
   */
  function validateDataIntegrity(originalData, conversionResult, validation) {
    devLog('log', '🔍 Validating data integrity...');

    if (!conversionResult.converted || typeof conversionResult.converted !== 'object') {
      validation.warnings.push('Cannot validate data integrity - invalid converted data structure');
      return;
    }

    const converted = conversionResult.converted;
    let hasNegativeValues = false;
    let hasNonNumericValues = false;
    let totalResponses = 0;

    // Check each converted value
    Object.entries(converted).forEach(([key, value]) => {
      if (typeof value !== 'number') {
        validation.errors.push(`Non-numeric value found: ${key} = ${value} (${typeof value})`);
        hasNonNumericValues = true;
      } else {
        if (value < 0) {
          validation.errors.push(`Negative value found: ${key} = ${value}`);
          hasNegativeValues = true;
        }
        totalResponses += value;
      }
    });

    // Check if we have reasonable response counts
    if (totalResponses === 0) {
      validation.warnings.push('Total response count is zero - survey may have no responses');
    } else if (totalResponses < 0) {
      validation.errors.push(`Invalid total response count: ${totalResponses}`);
    } else {
      validation.info.push(`Total responses validated: ${totalResponses}`);
    }

    // Compare with original data if available
    if (originalData && typeof originalData === 'object') {
      const originalTotal = Object.values(originalData).reduce((sum, val) => {
        return sum + (typeof val === 'number' ? val : 0);
      }, 0);

      if (Math.abs(originalTotal - totalResponses) > 0.01) { // Allow for small floating point differences
        validation.warnings.push(`Response count mismatch: original=${originalTotal}, converted=${totalResponses}`);
      } else {
        validation.info.push('Response count consistency verified');
      }
    }

    validation.info.push('Data integrity validation completed');
  }

  /**
   * Validate scale consistency and completeness
   */
  function validateScaleConsistency(conversionResult, context, validation) {
    devLog('log', '🔍 Validating scale consistency...');

    if (!conversionResult.converted || typeof conversionResult.converted !== 'object') {
      validation.warnings.push('Cannot validate scale consistency - invalid converted data');
      return;
    }

    const convertedKeys = Object.keys(conversionResult.converted);
    
    // Detect expected scale from used_mapping or assume 1-5
    let expectedScale = ['1', '2', '3', '4', '5']; // default
    if (context?.usedMapping) {
      const scaleInfo = detectScale(context.usedMapping);
      expectedScale = scaleInfo.scale;
    }

    // Check for missing scale positions
    const missingPositions = expectedScale.filter(pos => !convertedKeys.includes(pos));
    const extraPositions = convertedKeys.filter(key => !expectedScale.includes(key));

    if (missingPositions.length > 0) {
      validation.warnings.push(`Missing scale positions: ${missingPositions.join(', ')} (filled with zero)`);
    }

    if (extraPositions.length > 0) {
      validation.warnings.push(`Unexpected scale positions: ${extraPositions.join(', ')}`);
    }

    // Check if keys are properly ordered
    const numericKeys = convertedKeys.filter(key => /^\d+$/.test(key)).map(Number).sort((a, b) => a - b);
    if (numericKeys.length > 0) {
      const isConsecutive = numericKeys.every((val, i) => i === 0 || val === numericKeys[i-1] + 1);
      if (!isConsecutive) {
        validation.warnings.push(`Scale positions are not consecutive: ${numericKeys.join(', ')}`);
      } else {
        validation.info.push(`Scale consistency verified: ${numericKeys.join('-')} scale`);
      }
    }

    validation.info.push('Scale consistency validation completed');
  }

  /**
   * Validate response count reasonableness
   */
  function validateResponseCounts(originalData, conversionResult, validation) {
    devLog('log', '🔍 Validating response counts...');

    if (!conversionResult.converted) {
      return;
    }

    const values = Object.values(conversionResult.converted).filter(v => typeof v === 'number');
    if (values.length === 0) {
      validation.warnings.push('No numeric response counts found');
      return;
    }

    const total = values.reduce((sum, val) => sum + val, 0);
    const max = Math.max(...values);
    const min = Math.min(...values);

    // Basic reasonableness checks
    if (total > 10000) {
      validation.warnings.push(`Very high response count: ${total} - please verify data accuracy`);
    }

    if (max > total * 0.9) {
      validation.warnings.push(`Single rating dominates responses: ${max}/${total} (${Math.round(max/total*100)}%)`);
    }

    if (min === max && total > 0) {
      validation.warnings.push('All rating positions have identical counts - unusual distribution');
    }

    // Distribution analysis
    const nonZeroValues = values.filter(v => v > 0);
    if (nonZeroValues.length === 1) {
      validation.warnings.push('Only one rating position has responses - limited distribution');
    }

    validation.info.push(`Response count analysis: total=${total}, range=${min}-${max}, positions=${nonZeroValues.length}/${values.length}`);
  }

  /**
   * Validate mapping quality and coverage
   */
  function validateMappingQuality(conversionResult, context, validation) {
    devLog('log', '🔍 Validating mapping quality...');

    if (conversionResult.conversionType === 'no_conversion') {
      validation.info.push('No mapping validation needed - data already in correct format');
      return;
    }

    if (conversionResult.conversionType === 'fallback') {
      validation.warnings.push('Fallback conversion used - no mapping validation possible');
      validation.recommendations.push('Consider adding normalized_label_map or improving used_mapping compatibility');
      return;
    }

    // Check normalized_label_map quality (for two-step conversion)
    if (context?.normalizedLabelMap && conversionResult.conversionType === 'two_step') {
      const normalizedKeys = Object.keys(context.normalizedLabelMap);
      const normalizedValues = Object.values(context.normalizedLabelMap);
      
      if (normalizedKeys.length === 0) {
        validation.warnings.push('normalized_label_map is empty');
      }

      // Check for duplicate mappings
      const uniqueValues = [...new Set(normalizedValues)];
      if (uniqueValues.length < normalizedValues.length) {
        validation.warnings.push('normalized_label_map has duplicate target values');
      }

      validation.info.push(`normalized_label_map quality: ${normalizedKeys.length} mappings, ${uniqueValues.length} unique targets`);
    }

    // Check used_mapping quality
    if (context?.usedMapping) {
      const usedKeys = Object.keys(context.usedMapping);
      const usedValues = Object.values(context.usedMapping);
      
      if (usedKeys.length === 0) {
        validation.warnings.push('used_mapping is empty');
      }

      // Check for reasonable numeric values
      const numericValues = usedValues.filter(v => typeof v === 'number');
      if (numericValues.length < usedValues.length) {
        validation.warnings.push('used_mapping contains non-numeric values');
      }

      const valueRange = numericValues.length > 0 ? {
        min: Math.min(...numericValues),
        max: Math.max(...numericValues)
      } : null;

      if (valueRange && (valueRange.min < 1 || valueRange.max > 10)) {
        validation.warnings.push(`used_mapping has unusual scale range: ${valueRange.min}-${valueRange.max}`);
      }

      validation.info.push(`used_mapping quality: ${usedKeys.length} mappings, range=${valueRange?.min}-${valueRange?.max}`);
    }

    validation.info.push('Mapping quality validation completed');
  }

  /**
   * Calculate performance and quality metrics
   */
  function calculatePerformanceMetrics(originalData, conversionResult, validation) {
    devLog('log', '📊 Calculating performance metrics...');

    const metrics = {
      conversionSuccess: conversionResult?.success || false,
      conversionType: conversionResult?.conversionType || 'unknown',
      dataPoints: 0,
      completeness: 0,
      qualityScore: 0
    };

    if (conversionResult?.converted) {
      const converted = conversionResult.converted;
      metrics.dataPoints = Object.keys(converted).length;
      
      // Calculate completeness (non-zero values)
      const values = Object.values(converted).filter(v => typeof v === 'number');
      const nonZeroValues = values.filter(v => v > 0);
      metrics.completeness = values.length > 0 ? Math.round((nonZeroValues.length / values.length) * 100) : 0;
      
      // Calculate quality score based on various factors
      let qualityScore = 100;
      qualityScore -= validation.errors.length * 20; // Major penalty for errors
      qualityScore -= validation.warnings.length * 5; // Minor penalty for warnings
      qualityScore = Math.max(0, qualityScore); // Don't go below 0
      
      metrics.qualityScore = qualityScore;
    }

    validation.metrics = metrics;
    
    devLog('log', '📊 Performance metrics calculated:', metrics);
    validation.info.push(`Quality metrics: ${metrics.dataPoints} data points, ${metrics.completeness}% completeness, ${metrics.qualityScore}% quality score`);
  }

  /**
   * Generate actionable recommendations based on validation results
   */
  function generateRecommendations(validation) {
    devLog('log', '💡 Generating recommendations...');

    const recommendations = [];

    // Error-based recommendations
    if (validation.errors.length > 0) {
      recommendations.push('🚨 Critical: Fix validation errors before proceeding with survey analysis');
    }

    // Warning-based recommendations
    if (validation.warnings.length > 3) {
      recommendations.push('⚠️ Multiple warnings detected - consider reviewing data quality and mappings');
    }

    // Quality-based recommendations
    if (validation.metrics?.qualityScore < 70) {
      recommendations.push('📈 Low quality score - consider improving label mappings or data structure');
    }

    if (validation.metrics?.completeness < 80) {
      recommendations.push('📊 Low completeness - verify all rating scale positions are properly mapped');
    }

    // Conversion-specific recommendations
    if (validation.metrics?.conversionType === 'fallback') {
      recommendations.push('🔧 Using fallback conversion - add normalized_label_map for better accuracy');
    }

    if (validation.metrics?.conversionType === 'two_step') {
      recommendations.push('✅ Two-step conversion working well - consider this approach for similar surveys');
    }

    // Performance recommendations
    if (validation.metrics?.dataPoints > 10) {
      recommendations.push('⚡ Large scale detected - monitor performance for surveys with many rating levels');
    }

    // Add default recommendation if none generated
    if (recommendations.length === 0) {
      recommendations.push('✅ Validation passed - data processing appears to be working correctly');
    }

    validation.recommendations = recommendations;
    
    devLog('log', '💡 Generated recommendations:', recommendations);
  }

  /**
   * Convert distribution keys using normalized label mapping
   * Handles new structure surveys with localized keys (e.g., Chinese) that need conversion to English
   */
  function convertDistributionKeysUsingReverseMapping(distributionData, normalizedLabelMap, questionKey) {
    devLog('log', '🔄 Converting distribution keys using normalized label mapping...', { 
      questionKey: questionKey?.substring(0, 100) + '...',
      originalKeysCount: Object.keys(distributionData).length,
      normalizedMappingKeysCount: Object.keys(normalizedLabelMap || {}).length 
    });

    if (!normalizedLabelMap || typeof normalizedLabelMap !== 'object') {
      devLog('error', '❌ No normalized label mapping available for conversion');
      return { success: false, error: 'No normalized label mapping available' };
    }

    const convertedData = {};
    let conversionsCount = 0;
    let failedConversions = [];

    // Convert each distribution key using normalized mapping
    for (const [originalKey, value] of Object.entries(distributionData)) {
      const englishKey = normalizedLabelMap[originalKey];
      
      if (englishKey) {
        convertedData[englishKey] = value;
        conversionsCount++;
        devLog('log', `✅ Converted: "${originalKey}" → "${englishKey}" (${value} responses)`);
      } else {
        // Keep original key if no mapping found
        convertedData[originalKey] = value;
        failedConversions.push(originalKey);
        devLog('warn', `⚠️ No mapping found for: "${originalKey}" - keeping original`);
      }
    }

    const result = {
      success: conversionsCount > 0,
      convertedData,
      conversionsCount,
      failedConversions,
      totalKeys: Object.keys(distributionData).length
    };

    devLog('log', '🎯 Normalized mapping conversion result:', {
      success: result.success,
      conversionsCount,
      failedConversions: failedConversions.length,
      convertedKeys: Object.keys(convertedData)
    });

    return result;
  }

  /**
   * Universal Question Processor - handles all survey formats with comprehensive validation
   * Replaces the old Phase 2 processor with enhanced capabilities
   */
  function processQuestionNewStructure(questionKey, questionData, context) {
    devLog('log', '🚀 Processing question using UNIVERSAL processor...', { 
      questionKey: questionKey?.substring(0, 100) + '...',
      hasNormalizedMapping: !!context.normalizedLabelMap,
      hasUsedMapping: !!context.usedMapping,
      subFormat: context.subFormat 
    });

    let originalDistribution = null;
    
    try {
      // Step 1: Extract original distribution data
      originalDistribution = extractRatingsData(questionData, context.subFormat);
      if (!originalDistribution || Object.keys(originalDistribution).length === 0) {
        devLog('error', '❌ Failed to extract ratings data in universal processor');
        return { success: false, error: 'Failed to extract ratings data' };
      }

      devLog('log', '📊 Extracted original distribution:', { 
        keys: Object.keys(originalDistribution),
        keysSample: Object.keys(originalDistribution).slice(0, 5),
        totalResponses: Object.values(originalDistribution).reduce((sum, val) => sum + val, 0)
      });

      // Step 2: Detect what type of conversion is needed
      const distributionKeys = Object.keys(originalDistribution);
      const conversionPlan = detectConversionNeeded(distributionKeys, context);

      devLog('log', '🎯 Conversion plan determined:', {
        type: conversionPlan.type,
        reason: conversionPlan.reason,
        originalKeysCount: distributionKeys.length
      });

      // Step 3: Execute universal conversion
      const conversionResult = universalConvertDistribution(originalDistribution, conversionPlan);
      
      if (!conversionResult.success) {
        devLog('error', '❌ Universal conversion failed:', conversionResult.error || 'Unknown error');
        return { 
          success: false, 
          error: conversionResult.error || 'Universal conversion failed', 
          originalDistribution,
          conversionPlan 
        };
      }

      const convertedDistribution = conversionResult.converted;
      devLog('log', '✅ Universal conversion successful:', {
        conversionType: conversionResult.conversionType,
        originalKeys: Object.keys(originalDistribution),
        convertedKeys: Object.keys(convertedDistribution),
        finalDistribution: convertedDistribution
      });

      // Step 4: Comprehensive validation
      const validationResult = validateConversionResult(originalDistribution, conversionResult, context);
      
      devLog('log', '📋 Validation completed:', {
        isValid: validationResult.isValid,
        errorsCount: validationResult.errors.length,
        warningsCount: validationResult.warnings.length,
        qualityScore: validationResult.metrics.qualityScore
      });

      // Step 5: Calculate metrics using converted data
      const totalResponses = Object.values(convertedDistribution).reduce((sum, val) => sum + val, 0);
      
      // Calculate average using the used_mapping numeric values or fallback to numeric keys
      let weightedSum = 0;
      let mappedResponses = 0;
      
      for (const [key, count] of Object.entries(convertedDistribution)) {
        let numericValue;
        
        // Try used_mapping first, then parse key as number
        if (context.usedMapping && context.usedMapping[key] !== undefined) {
          numericValue = context.usedMapping[key];
        } else if (/^\d+$/.test(key)) {
          numericValue = parseInt(key, 10);
        }
        
        if (numericValue !== undefined && typeof numericValue === 'number') {
          weightedSum += numericValue * count;
          mappedResponses += count;
        }
      }
      
      const calculatedAverage = mappedResponses > 0 ? (weightedSum / mappedResponses) : 0;

      // Step 6: Build comprehensive result
      const processedQuestion = {
        question_text: questionKey,
        ratings_breakdown: convertedDistribution,
        response_count: totalResponses,
        average_rating: calculatedAverage,
        processing_method: 'universal_processor_v2',
        conversion_info: {
          conversion_type: conversionResult.conversionType,
          original_keys: Object.keys(originalDistribution),
          converted_keys: Object.keys(convertedDistribution),
          conversion_plan: conversionPlan,
          validation_summary: {
            is_valid: validationResult.isValid,
            quality_score: validationResult.metrics.qualityScore,
            completeness: validationResult.metrics.completeness,
            errors_count: validationResult.errors.length,
            warnings_count: validationResult.warnings.length
          }
        },
        // Include validation details for debugging
        validation_details: {
          errors: validationResult.errors,
          warnings: validationResult.warnings,
          recommendations: validationResult.recommendations,
          metrics: validationResult.metrics
        }
      };

      devLog('log', '🎉 UNIVERSAL processing successful:', {
        question: questionKey?.substring(0, 50) + '...',
        conversionType: conversionResult.conversionType,
        totalResponses,
        averageScore: calculatedAverage.toFixed(2),
        qualityScore: validationResult.metrics.qualityScore,
        isValid: validationResult.isValid
      });

      // Log recommendations if any
      if (validationResult.recommendations.length > 0) {
        devLog('log', '💡 Processing recommendations:', validationResult.recommendations);
      }

      return { success: true, processedQuestion };

    } catch (error) {
      devLog('error', '💥 Error in universal processor:', error);
      return { 
        success: false, 
        error: error.message,
        stack: error.stack,
        context: {
          questionKey: questionKey?.substring(0, 100),
          hasOriginalData: !!originalDistribution,
          contextKeys: Object.keys(context)
        }
      };
    }
  }

  /**
   * Check if new structure processing should be used
   * Priority: Use new processor when reverse_label_map is available
   */
  function shouldUseNewStructureProcessor(context) {
    const hasNormalizedMapping = context.normalizedLabelMap && 
                                typeof context.normalizedLabelMap === 'object' && 
                                Object.keys(context.normalizedLabelMap).length > 0;
    
    const isNewFormat = context.mainFormat === 'new';
    
    devLog('log', '🔍 Checking new structure processor eligibility:', {
      hasNormalizedMapping,
      isNewFormat,
      normalizedMappingKeys: hasNormalizedMapping ? Object.keys(context.normalizedLabelMap).length : 0
    });

    return hasNormalizedMapping && isNewFormat;
  }

  // Convert new format to old format structure for compatibility (MODULAR VERSION)
  const adaptSurveyData = (analysisData) => {
    devLog('log', '🔄 Starting survey data adaptation...');
    
    const format = detectDataFormat(analysisData);
    devLog('log', `📊 Detected main format: ${format}`);
    
    if (format === 'old') {
      devLog('log', '✅ Old format detected, no adaptation needed');
      return analysisData;
    }
    
    if (format === 'new') {
      devLog('log', '🔄 New format detected, starting modular adaptation...');
      
      const { distribution, average_scores } = analysisData;
      
      // Early validation
      if (!distribution || !average_scores) {
        devLog('error', '🚨 Missing distribution or average_scores in new format');
        return analysisData;
      }
      
      // Step 1: Detect sub-format structure
      const subFormat = detectNewFormatSubType(distribution);
      devLog('log', `📊 Detected sub-format: ${subFormat}`);
      
      if (subFormat === 'unknown') {
        devLog('error', '🚨 Unknown sub-format, cannot process');
        return analysisData;
      }
      
      // Step 2: Extract used mapping for conversions
      const usedMapping = average_scores.used_mapping || {};
      devLog('log', '🗂️ Extracted used mapping:', {
        mappingKeys: Object.keys(usedMapping),
        mappingCount: Object.keys(usedMapping).length
      });

      // Step 3: Extract normalized label map if available
      const normalizedLabelMap = analysisData.average_scores?.normalized_label_map || {};
      devLog('log', '🔄 Extracted normalized label map:', {
        mapKeys: Object.keys(normalizedLabelMap),
        mapCount: Object.keys(normalizedLabelMap).length
      });

      // Determine if we should use the new structure processor
      const context = {
        mainFormat: format,
        subFormat: subFormat,
        usedMapping: usedMapping,
        normalizedLabelMap: normalizedLabelMap
      };

      if (shouldUseNewStructureProcessor(context)) {
        devLog('log', '🔄 Using NEW STRUCTURE processor for key conversion.');
        const questionKeys = Object.keys(distribution);
        const processedQuestions = [];
        let successCount = 0;
        let failureCount = 0;

        for (const questionKey of questionKeys) {
          const questionData = distribution[questionKey];
          const averageData = average_scores.averages[questionKey];

          const processedResult = processQuestionNewStructure(questionKey, questionData, context);

                     if (processedResult.success) {
             processedQuestions.push(processedResult.processedQuestion);
             successCount++;
             devLog('log', `✅ Question "${questionKey}" processed successfully by new processor.`);
           } else {
             failureCount++;
             devLog('error', `❌ Question "${questionKey}" failed to process by new processor: ${processedResult.error}`, {
               originalData: questionData,
               averageData: averageData
             });
           }
        }

        // Step 4: Create overall_summary from processed data
        const overall_summary = {
          total_responses: processedQuestions.length > 0 ? processedQuestions[0].response_count : 0,
          total_questions: processedQuestions.length,
          overall_average: average_scores.overall_average || 0
        };

        // Step 5: Final adaptation results
        devLog('log', '🎯 Modular adaptation successful (NEW STRUCTURE):', {
          totalQuestions: questionKeys.length,
          successfullyProcessed: successCount,
          failed: failureCount,
          finalQuestionsCount: processedQuestions.length,
          totalResponses: overall_summary.total_responses,
          overallAverage: overall_summary.overall_average,
          sampleRatingsKeys: processedQuestions[0]?.ratings ? Object.keys(processedQuestions[0].ratings) : []
        });

        if (processedQuestions.length === 0) {
          devLog('error', '🚨 No questions successfully processed by new processor, returning original data');
          return analysisData;
        }

        return {
          ...analysisData,
          questions: processedQuestions,
          overall_summary
        };
      } else {
        devLog('log', '🔄 Using OLD STRUCTURE processor for key conversion.');
        // For old structure, we just need to process questions as before
        const questionKeys = Object.keys(distribution);
        const processedQuestions = [];
        let successCount = 0;
        let failureCount = 0;

        for (const questionKey of questionKeys) {
          const questionData = distribution[questionKey];
          const averageData = average_scores.averages[questionKey];

          const processedResult = processQuestion(
            questionKey,
            questionData,
            averageData,
            usedMapping,
            subFormat
          );

          if (processedResult.processing_status === 'success') {
            // Remove metadata before adding to questions array (not needed for old format compatibility)
            const { metadata, processing_status, error_message, ...cleanQuestion } = processedResult;
                         processedQuestions.push(cleanQuestion);
             successCount++;
             devLog('log', `✅ Question "${questionKey}" processed successfully by old processor.`);
           } else {
             failureCount++;
             devLog('error', `❌ Question "${questionKey}" failed to process by old processor: ${processedResult.processing_status}`, {
               errorMessage: processedResult.error_message
             });
           }
        }

        // Step 4: Create overall_summary from processed data
        const overall_summary = {
          total_responses: processedQuestions.length > 0 ? processedQuestions[0].response_count : 0,
          total_questions: processedQuestions.length,
          overall_average: average_scores.overall_average || 0
        };

        // Step 5: Final adaptation results
        devLog('log', '🎯 Modular adaptation successful (OLD STRUCTURE):', {
          totalQuestions: questionKeys.length,
          successfullyProcessed: successCount,
          failed: failureCount,
          finalQuestionsCount: processedQuestions.length,
          totalResponses: overall_summary.total_responses,
          overallAverage: overall_summary.overall_average,
          sampleRatingsKeys: processedQuestions[0]?.ratings_breakdown ? Object.keys(processedQuestions[0].ratings_breakdown) : []
        });

        if (processedQuestions.length === 0) {
          devLog('error', '🚨 No questions successfully processed by old processor, returning original data');
          return analysisData;
        }

        return {
          ...analysisData,
          questions: processedQuestions,
          overall_summary
        };
      }
    }
    
    // Unknown format
    devLog('warn', '⚠️ Unknown main format, returning original data');
    return analysisData;
  };

  // Calculate accurate values using our utility functions (cached with useMemo)
  const calculatedData = useMemo(() => {
    // Only calculate if we have valid imported survey data
    if (!survey?.isImported || !survey?.analysisData) {
      return null;
    }

    // 🔍 QUICK TEST: Log raw survey data structure
    console.log(`🔍 QUICK TEST - Raw survey data for "${survey.name}":`);
    console.log('Raw distribution data:', survey.analysisData.distribution);
    if (survey.analysisData.distribution) {
      const firstQuestionKey = Object.keys(survey.analysisData.distribution)[0];
      if (firstQuestionKey) {
        console.log('First question key:', firstQuestionKey);
        console.log('First question data:', survey.analysisData.distribution[firstQuestionKey]);
        console.log('Data type:', typeof survey.analysisData.distribution[firstQuestionKey]);
        console.log('Is array:', Array.isArray(survey.analysisData.distribution[firstQuestionKey]));
      }
    }
    console.log('Raw average_scores data:', survey.analysisData.average_scores);

    // Adapt the data to handle both old and new formats
    const adaptedData = adaptSurveyData(survey.analysisData);
    
    // Check if adapted data has required structure
    if (!adaptedData?.questions || !adaptedData?.overall_summary) {
      return null;
    }

    const questions = adaptedData.questions;
    
    console.log('🔄 Recalculating survey data for accuracy...');
    
    // Process each question with accurate calculations
    const processedQuestions = questions.map(question => {
      const validation = validateRatingsBreakdown(question.ratings_breakdown, question.response_count);
      const calculatedAverage = calculateQuestionAverage(question.ratings_breakdown);
      
      return {
        ...question,
        calculatedAverage,
        validation,
        isValidData: validation.isValid && calculatedAverage !== null
      };
    });
    
    // Calculate overall average from our calculated question averages
    const calculatedOverallAverage = calculateOverallAverage(questions);
    
    return {
      questions: processedQuestions,
      overallAverage: calculatedOverallAverage || 0,
      validQuestionCount: processedQuestions.filter(q => q.isValidData).length
    };
  }, [survey?.analysisData, survey?.isImported]);

  // Data validation helper
  const isValidAnalysisData = (data) => {
    // Try to adapt the data first
    const adaptedData = adaptSurveyData(data);
    
    return adaptedData && 
           adaptedData.overall_summary && 
           typeof adaptedData.overall_summary.overall_average === 'number' &&
           adaptedData.questions && 
           Array.isArray(adaptedData.questions) &&
           adaptedData.questions.every(q => 
             q.question_text && 
             typeof q.average_rating === 'number' &&
             q.ratings_breakdown &&
             typeof q.ratings_breakdown === 'object'
           );
  };

  // Error handling for different survey types
  if (!survey) {
    return (
      <div className="indirect_method_recent_survey_history_container">
        <div className="indirect_method_recent_survey_history_error">
          <h2>Error</h2>
          <p>No survey data provided.</p>
          <button onClick={onBack}>← Back to Survey List</button>
        </div>
      </div>
    );
  }

  // Handle non-imported surveys (legacy or manual entries)
  if (!survey.isImported) {
    return (
      <div className="indirect_method_recent_survey_history_container">
        <div className="indirect_method_recent_survey_history_error">
          <h2>Survey Not Available</h2>
          <p>This survey was not imported through our system and detailed results are not available.</p>
          <button onClick={onBack}>← Back to Survey List</button>
        </div>
      </div>
    );
  }

  // Handle missing or invalid analysis data
  if (!survey.analysisData || !isValidAnalysisData(survey.analysisData)) {
    return (
      <div className="indirect_method_recent_survey_history_container">
        <div className="indirect_method_recent_survey_history_error">
          <h2>Analysis Data Unavailable</h2>
          <p>The analysis data for this survey is missing or corrupted.</p>
          <button onClick={onBack}>← Back to Survey List</button>
        </div>
      </div>
    );
  }

  // Handle case where calculation failed
  if (!calculatedData) {
    return (
      <div className="indirect_method_recent_survey_history_container">
        <div className="indirect_method_recent_survey_history_error">
          <h2>Calculation Error</h2>
          <p>Unable to process survey data for display.</p>
          <button onClick={onBack}>← Back to Survey List</button>
        </div>
      </div>
    );
  }

  // Main render for valid imported survey with analysis data
  if (survey.isImported && survey.analysisData && calculatedData) {
    const adaptedData = adaptSurveyData(survey.analysisData);
    const totalResponses = adaptedData.overall_summary.total_responses;

    // Transform ratings breakdown to percentage distribution for stacked bars
    const generateRatingBars = (question) => {
      // Check if question data is valid
      if (!question.isValidData) {
        return (
          <div className="indirect_method_recent_survey_history_rating_bars_container">
            <div className="indirect_method_recent_survey_history_rating_error">
              Data Error: {question.validation.error || 'Invalid ratings data'}
            </div>
          </div>
        );
      }

      const ratingsBreakdown = question.ratings_breakdown;
      const calculatedAverage = question.calculatedAverage;
      const total = Object.values(ratingsBreakdown).reduce((sum, count) => sum + count, 0);
      
      if (total === 0) {
        return (
          <div className="indirect_method_recent_survey_history_rating_bars_container">
            <div className="indirect_method_recent_survey_history_rating_error">
              No response data available
            </div>
          </div>
        );
      }

      // Calculate percentages
      const percentages = [
        (ratingsBreakdown["1"] || 0) / total * 100, // Red (Strongly Disagree)
        (ratingsBreakdown["2"] || 0) / total * 100, // Orange (Disagree)
        (ratingsBreakdown["3"] || 0) / total * 100, // Yellow (Neutral)
        (ratingsBreakdown["4"] || 0) / total * 100, // Light Blue (Agree)
        (ratingsBreakdown["5"] || 0) / total * 100  // Green (Strongly Agree)
      ];

      const colors = ['#ef4444', '#f97316', '#fbbf24', '#84cc16', '#10b981'];

      return (
        <div className="indirect_method_recent_survey_history_rating_bars_container">
          <div className="indirect_method_recent_survey_history_rating_bars">
            {percentages.map((percentage, index) => (
              percentage > 0 && (
                <div
                  key={index}
                  className="indirect_method_recent_survey_history_rating_bar"
                  style={{
                    backgroundColor: colors[index],
                    width: `${Math.max(percentage * 2, 2)}px`, // Scale for visual effect, min 2px
                    height: '20px'
                  }}
                  title={`Rating ${index + 1}: ${Math.round(percentage)}% (${ratingsBreakdown[String(index + 1)] || 0} responses)`}
                />
              )
            ))}
          </div>
          <span className="indirect_method_recent_survey_history_rating_score">
            {roundToTwo(calculatedAverage).toFixed(2)}
          </span>
        </div>
      );
    };

    return (
      <div className="indirect_method_recent_survey_history_container">
        {/* Header */}
        <div className="indirect_method_recent_survey_history_header">
          <button 
            className="indirect_method_recent_survey_history_back_to_survey_list"
            onClick={onBack}
          >
            ← Back to Survey list
          </button>
          <h1 className="indirect_method_recent_survey_history_title">
            Survey Results: {survey.survey_name}
          </h1>
        </div>

        {/* Average Rating Gauge */}
        <div className="indirect_method_recent_survey_history_average_rating_section">
          <div className="indirect_method_recent_survey_history_rating_gauge_container">
            <svg width="200" height="120" viewBox="0 0 200 120">
              {/* Background arc */}
              <path
                d="M 20 100 A 80 80 0 0 1 180 100"
                stroke="#e5e7eb"
                strokeWidth="12"
                fill="none"
                strokeLinecap="round"
              />
              {/* Colored rating arc */}
              <path
                d="M 20 100 A 80 80 0 0 1 180 100"
                stroke="url(#ratingGradient)"
                strokeWidth="12"
                fill="none"
                strokeDasharray={`${(calculatedData.overallAverage / 5) * 251} 251`}
                strokeLinecap="round"
              />
              {/* Gradient definition */}
              <defs>
                <linearGradient id="ratingGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#ef4444" />
                  <stop offset="25%" stopColor="#f97316" />
                  <stop offset="50%" stopColor="#fbbf24" />
                  <stop offset="75%" stopColor="#84cc16" />
                  <stop offset="100%" stopColor="#10b981" />
                </linearGradient>
              </defs>
              {/* Rating text */}
              <text x="100" y="85" textAnchor="middle" className="indirect_method_recent_survey_history_rating_value">
                {roundToTwo(calculatedData.overallAverage).toFixed(2)}
              </text>
            </svg>
            <div className="indirect_method_recent_survey_history_rating_label">
              Average Rating ({totalResponses} responses)
            </div>
          </div>
        </div>

        {/* Results Breakdown */}
        <div className="indirect_method_recent_survey_history_results_breakdown_section">
          <h2 className="indirect_method_recent_survey_history_breakdown_title">Results Breakdown</h2>
          
          <div className="indirect_method_recent_survey_history_results_table_container">
            <table className="indirect_method_recent_survey_history_results_table">
              <thead>
                <tr>
                  <th className="indirect_method_recent_survey_history_statements_header">Questions</th>
                  <th className="indirect_method_recent_survey_history_rating_header">Rating Distribution</th>
                </tr>
              </thead>
              <tbody>
                {calculatedData.questions.map((question, index) => (
                  <tr key={index} className="indirect_method_recent_survey_history_results_row">
                    <td className="indirect_method_recent_survey_history_statement_cell">
                      {question.question_text}
                    </td>
                    <td className="indirect_method_recent_survey_history_rating_cell">
                      {generateRatingBars(question)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="indirect_method_recent_survey_history_summary_section">
          <div className="indirect_method_recent_survey_history_summary_stats">
            <div className="indirect_method_recent_survey_history_stat">
              <span className="indirect_method_recent_survey_history_stat_value">{calculatedData.questions.length}</span>
              <span className="indirect_method_recent_survey_history_stat_label">Questions</span>
            </div>
            <div className="indirect_method_recent_survey_history_stat">
              <span className="indirect_method_recent_survey_history_stat_value">{totalResponses}</span>
              <span className="indirect_method_recent_survey_history_stat_label">Total Responses</span>
            </div>
            <div className="indirect_method_recent_survey_history_stat">
              <span className="indirect_method_recent_survey_history_stat_value">
                {roundToTwo(calculatedData.overallAverage).toFixed(2)}
              </span>
              <span className="indirect_method_recent_survey_history_stat_label">Overall Average</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="indirect_method_recent_survey_history_action_buttons_section">
          <button className="indirect_method_recent_survey_history_export_button">
            📊 Export Results
          </button>
          <button className="indirect_method_recent_survey_history_share_results_button">
            📤 Share Results
          </button>
        </div>
      </div>
    );
  }

  // Fallback for any other cases
  return (
    <div className="indirect_method_recent_survey_history_container">
      <div className="indirect_method_recent_survey_history_error">
        <h2>Unable to Display Survey</h2>
        <p>This survey cannot be displayed at this time.</p>
        <button onClick={onBack}>← Back to Survey List</button>
      </div>
    </div>
  );
};

export default IndirectMethod_RecentSurveyHistory_SurveyResult; 